/**
 * Parse a Google Places formatted address down to the street line only.
 *
 * Strips city, state, zip, and country. Keeps street number, name, and unit.
 *
 * Examples:
 *   "747 St Charles Ave, New Orleans, LA 70130, USA" → "747 St Charles Ave"
 *   "Wilco, 1111 Canal St, New Orleans, LA, USA"     → "1111 Canal St"
 *   "Cafe Du Monde, 800 Decatur St, New Orleans, LA" → "800 Decatur St"
 *   "Some Place"                                     → "Some Place"
 *
 * Strategy: split on commas, return the first segment that contains a
 * digit (street numbers always have one). If no segment has a digit,
 * fall back to the first segment.
 */
export function parseStreetAddress(address: string): string {
  if (!address) return '';
  const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return '';
  const street = parts.find((p) => /\d/.test(p));
  return street || parts[0];
}

/**
 * Parse the venue name (non-numeric prefix segment) from a full address.
 *
 * Examples:
 *   "House of Blues, 225 Decatur St, New Orleans, LA, USA" → "House of Blues"
 *   "747 St Charles Ave, New Orleans, LA 70130, USA"       → ""
 *   "Cafe Du Monde, 800 Decatur St, New Orleans, LA"       → "Cafe Du Monde"
 */
export function parseVenueName(address: string): string {
  if (!address) return '';
  const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return '';
  const first = parts[0];
  // If first segment has a digit, it's a street address — no separate venue.
  if (/\d/.test(first)) return '';
  return first;
}
